import {AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import { BusinessNumberSearchFn, getEntityType, } from '@portal-workspace/grow-ui-library';
import {BusinessNumberSearchValue, compareMatch, fromIndividualFormValue, IndividualFormValue, MemberValue, toIndividualFormValue} from '@portal-workspace/grow-shared-library';
import { PersonalAssetComponentEvent, PersonalAssetsComponent } from './personal-assets.component';
import { PersonalLiabilitiesComponentEvent, PersonalLiabilitiesComponent } from './personal-liabilities.component';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import { NG_VALUE_ACCESSOR, Validators, FormBuilder, FormGroup, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {Subject, Subscription} from 'rxjs';
import {EntityMember, IndividualMember} from '@portal-workspace/grow-shared-library';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {IndividualOrEntity} from '@portal-workspace/grow-shared-library';
import {UntilDestroy} from '@ngneat/until-destroy';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EntityTypeComponent } from '../entity-type-component/entity-type.component';
import { BusinessNumberSearchComponent } from '../business-number-search-component/business-number-search.component';
import { MatButtonModule } from '@angular/material/button';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';


export interface MemberComponentEvent {
  valid: boolean;
  entries: (IndividualMember | EntityMember)[];
}

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'member',
    templateUrl: './member.component.html',
    styleUrls: ['./member.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MemberComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => MemberComponent) },
    ],
    standalone: true,
    imports: [MatButtonToggleModule, FormsModule, ReactiveFormsModule, MarkDirective, IndividualFormComponent, MatButtonModule, PersonalAssetsComponent, PersonalLiabilitiesComponent, BusinessNumberSearchComponent, EntityTypeComponent, MatFormFieldModule]
})
export class MemberComponent extends AbstractControlValueAccessor<MemberValue> implements OnInit, AfterViewInit, Mark {

  markObservable: Subject<boolean> = new Subject<boolean>();

  formGroup: FormGroup;
  formArray: FormArray;

  subscription?: Subscription;
  subscriptions: Subscription[] = [];

  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  @Input({required: false}) showAssetsAndLiabilities = true;
  @Input({required: false}) showAddressForm = true;
  @Input({required: false}) showIdentification = false;
  @Output() events: EventEmitter<MemberComponentEvent>;

  disableControls:boolean = true;
  entityTypeReadonly = true;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.events = new EventEmitter<MemberComponentEvent>();
    this.formBuilder = formBuilder;
    this.formArray = formBuilder.array([]);
    this.formGroup = formBuilder.group({
      members: this.formArray,
    });
  }


  ngOnInit(): void {
    setupUntilDestroy(this);
    this.subscription = this.formArray.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(array => {
        const entries = this.formArrayToMemberValue();
        if (this.formArray.invalid) {
          this.propagateChange(null);
        } else {
          this.propagateChange(entries);
        }
        this.events.emit({valid: this.formArray.valid, entries});
      })
    ).subscribe();
  }

  ngAfterViewInit() {
    setTimeout(()=> {
    });
  }

  doWriteValue(v: MemberValue | null | undefined): void | MemberValue {
    if (v) {
      for (const o of v) {
        this.addMember(o);
      }
    } else {
      this.addMember();
    }
    return undefined;
  }

  memberFormGroups(): FormGroup[] {
    return this.formArray.controls as FormGroup[];
  }

  memberFormGroupFormControl(memberFormGroup: FormGroup, controlName: string) {
    return memberFormGroup.controls[controlName] as FormControl;
  }

  formArrayToMemberValue(): (IndividualMember | EntityMember)[] {
    return (this.formArray.controls as FormGroup[]).map((fg: FormGroup) => {
      const v = fg.value;
      if (v.type === 'Individual') {
        const individual = fromIndividualFormValue(v.individual);
        return {
          kind: 'Member',
          type: 'Individual',
          ...(individual ?? {}),
          personalAssets: v.personalAssets ?? null,
          personalLiabilities: v.personalLiabilities ?? null,
        } as IndividualMember;
      }
      return v as EntityMember;
    });
  }

  onPersonalAssetEvent($event: PersonalAssetComponentEvent, memberFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) { // there are assets entries
      const formControl = this.memberFormGroupFormControl(memberFormGroup, 'personalAssets');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.memberFormGroupFormControl(memberFormGroup, 'personalAssets');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }

  onPersonalLiabilitiesEvent($event: PersonalLiabilitiesComponentEvent, memberFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) {
      const formControl = this.memberFormGroupFormControl(memberFormGroup, 'personalLiabilities');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.memberFormGroupFormControl(memberFormGroup, 'personalLiabilities');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }

  removeMember(memberFormGroup: FormGroup) {
    const index = this.formArray.controls.indexOf(memberFormGroup);
    if (index !== -1) {
      this.formArray.removeAt(index);
    }
    this.events.emit({valid: this.formArray.valid, entries: this.formArrayToMemberValue()});
  }

  addMember(g?: Exclude<MemberValue, null>[number]) {
    const formControlIndividualOrEntity = this.formBuilder.control<IndividualOrEntity>('Entity', [Validators.required]);
    const formControlPersonalAssets = this.formBuilder.control(null);
    const formControlPersonalLiabilities = this.formBuilder.control(null);
    const formControlKind= this.formBuilder.control('Member');
    const formGroup = this.formBuilder.group({
      kind: formControlKind,
      type: formControlIndividualOrEntity,
      personalAssets: formControlPersonalAssets,
      personalLiabilities: formControlPersonalLiabilities,
    });
    if (g) {
      switch(g.type) {
        case 'Individual': {
          (formGroup.controls as any).type.setValue('Individual');
          this.switchToIndividualMember(formGroup, g);
          break;
        }
        case 'Entity': {
          (formGroup.controls as any).type.setValue('Entity');
          this.switchToEntityMember(formGroup, g);
          break;
        }
      }
    } else {
      this.switchToEntityMember(formGroup, g);
    }
    this.formArray.push(formGroup);

    const subscription = formControlIndividualOrEntity.valueChanges.pipe(
      delay(0),
      tap((r: IndividualOrEntity|null) => {
        switch (r) {
          case 'Individual': {
            this.switchToIndividualMember(formGroup);
            break;
          }
          case 'Entity': {
            this.switchToEntityMember(formGroup);
            break;
          }
        }
      })
    ).subscribe();
    this.subscriptions.push(subscription);
    this.events.emit({valid: this.formArray.valid, entries: this.formArrayToMemberValue()});
  }

  private switchToIndividualMember(formGroup: FormGroup, member?: IndividualMember) {
    const formControlIndividual = this.formBuilder.control<IndividualFormValue>(toIndividualFormValue(member), [Validators.required]);
    const formControlPersonalAssets = this.formBuilder.control(member ? member.personalAssets : null);
    const formControlPersonalLiabilities = this.formBuilder.control(member ? member.personalLiabilities : null);

    formGroup.removeControl('organisation');
    formGroup.removeControl('organisationType');
    formGroup.removeControl('acn');
    formGroup.removeControl('abn');

    formGroup.addControl('individual', formControlIndividual);
    formGroup.setControl('personalAssets', formControlPersonalAssets);
    formGroup.setControl('personalLiabilities', formControlPersonalLiabilities);
  }

  private switchToEntityMember(formGroup: FormGroup, member?: EntityMember) {
    const formControlOrganisation = this.formBuilder.control(member ? member.organisation : null, [Validators.required]); // business-search
    const formControlOrganisationType = this.formBuilder.control(member ? member.organisationType : { type: 'company'}, [Validators.required]); // entity-type-selection
    const formControlAcn = this.formBuilder.control(member ? member.acn : null );
    const formControlAbn = this.formBuilder.control(member ? member.abn : null);

    formGroup.removeControl('individual');
    formGroup.removeControl('personalAssets');
    formGroup.removeControl('personalLiabilities');

    formGroup.addControl('organisation', formControlOrganisation);
    formGroup.addControl('organisationType', formControlOrganisationType);
    formGroup.addControl('acn', formControlAcn);
    formGroup.addControl('abn', formControlAbn);

    this.subscriptions.push(formControlOrganisation.valueChanges.pipe(
      tap((r: BusinessNumberSearchValue) => {
        if (r) {
          const abn = r.abn;
          let acn = r.acn;
          if (acn) {
            // add leading zeros if less than 9 digits
            acn = 9 - acn.length > 0 ? '0'.repeat(9 - acn.length) + acn : acn;
            formControlAcn.setValue(acn);
          } else {
            formControlAcn.setValue('');
          }
          if (abn) {
            formControlAbn.setValue(abn);
          } else {
            formControlAbn.setValue('');
          }
          if (r.result) {
            const entityType = getEntityType(r.result)
            formControlOrganisationType.setValue(entityType)
            if (entityType?.type == 'other' || !entityType) {
              this.entityTypeReadonly = false
            } else {
              this.entityTypeReadonly = true
            }
          } else {
            this.entityTypeReadonly = !!formControlOrganisationType.value
          }
        }
      })
    ).subscribe());
  }

  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }
}
